// ScoringCriteria.js
import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { Header } from "./components/Header";
import { useParams, NavLink } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Navbar from "./components/Navbar";
import { saveCriteria, loadAll, loadOne } from "./CriteriaService";
import {
  defaultCriteria,
  FinancialCriteria,
  defaultMatrix,
} from "./criteriaDefaults";

const API_URL = "/api";
// ----------------- Deep Clone Helper -----------------
const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

// ----------------- Helper Functions -----------------
const getPointsFromMatrix = (matrix, importance, type) => {
  const row = matrix.find((m) => m.importance === importance);
  if (!row) return 0;
  const map = {
    Positive: row.positive,
    Baseline: row.baseline,
    "Negative (low)": row.negativeLow,
    "Negative (high)": row.negativeHigh,
  };
  return map[type] ?? 0;
};

const getMinMaxForParent = (children) => {
  if (!children || children.length === 0) return { min: 0, max: 0 };
  const points = children.map((c) => parseFloat(c.POINTS) || 0);
  return {
    min: Math.min(...points),
    max: Math.max(...points),
  };
};

const getTotalScore = (data) => {
  if (!Array.isArray(data)) return { minTotal: 0, maxTotal: 0 };
  return data.reduce(
    (totals, item) => {
      if (!item.CHILDREN || item.CHILDREN.length === 0) return totals;
      const points = item.CHILDREN.map((c) => parseFloat(c.POINTS) || 0);
      const min = Math.min(...points);
      const max = Math.max(...points);
      return {
        minTotal: totals.minTotal + min,
        maxTotal: totals.maxTotal + max,
      };
    },
    { minTotal: 0, maxTotal: 0 }
  );
};

// ----------------- Main Component -----------------
export default function ScoringCriteria() {
  const { id } = useParams();
  const [company] = useState({ COMPANYID: id, COMPANYNAME: "Demo Company" });
  const [criteria, setCriteria] = useState([]);
  const [savedCriteria, setSavedCriteria] = useState([]);
  const [matrix, setMatrix] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recordId, setRecordId] = useState(null);
  const [currentId, setCurrentId] = useState(null);
  const [financialCriteria, setFinancialCriteria] = useState([]);
  const [savedFinancialCriteria, setSavedFinancialCriteria] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [companyId, setCompanyId] = useState(null);

  // ----------------- Load Data -----------------
  useEffect(() => {
    async function boot() {
      try {
        const all = await loadAll();
        let record = null;

        // ✅ if id exists in route → fetch by it
        if (id) {
          record = all.find((r) => r._id === id);
          setRecordId(id);
        } else if (recordId) {
          record = all.find((r) => r._id === recordId);
        } else {
          const last = all && all.length ? all[all.length - 1] : null;
          record = last ?? null;
          setRecordId(last?._id || null);
        }

        if (record) {
          setCriteria(deepClone(record.defaultCriteria ?? defaultCriteria));
          setSavedCriteria(
            deepClone(record.defaultCriteria ?? defaultCriteria)
          );
          setFinancialCriteria(
            deepClone(record.financialCriteria ?? FinancialCriteria)
          );
          setSavedFinancialCriteria(
            deepClone(record.financialCriteria ?? FinancialCriteria)
          );
          setMatrix(deepClone(record.defaultMatrix ?? defaultMatrix));
          setSaveName(record.saveName ?? "");
        } else {
          // defaults if nothing found
          setCriteria(deepClone(defaultCriteria));
          setSavedCriteria(deepClone(defaultCriteria));
          setFinancialCriteria(deepClone(FinancialCriteria));
          setSavedFinancialCriteria(deepClone(FinancialCriteria));
          setMatrix(deepClone(defaultMatrix));
          setSaveName("");
        }
      } catch (err) {
        console.error("load error", err);
      }
    }

    boot();
  }, [id, recordId]);

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      try {
        const response = await fetch(`${API_URL}/getComapnyProfile/${id}`);
        if (!response.ok)
          throw new Error(`HTTP error! Status: ${response.status}`);
        const data = await response.json();
        setCompanyId(data?.COMPANYID);

        console.log("Data", data);
      } catch (err) {
        console.error("Error fetching company details:", err);
      }
    };
    fetchCompanyDetails();
  }, [id]);

  // ----------------- Handlers -----------------
  const handleParentChangeFinancial = (parentIdx, field, value) => {
    const updated = deepClone(financialCriteria);
    const parent = updated[parentIdx];
    if (field === "IMPORTANCE") {
      parent.IMPORTANCE = value;
      parent.CHILDREN = parent.CHILDREN.map((child) => ({
        ...child,
        IMPORTANCE: value,
        POINTS: getPointsFromMatrix(matrix, value, child.TYPE),
      }));
    } else {
      parent[field] = value;
    }
    updated[parentIdx] = parent;
    setFinancialCriteria(updated);
  };

  const handleChildChangeFinancial = (parentIdx, childIdx, field, value) => {
    const updated = deepClone(financialCriteria);
    const child = updated[parentIdx].CHILDREN[childIdx];
    if (field === "TYPE") {
      child.TYPE = value;
      child.POINTS = getPointsFromMatrix(
        matrix,
        updated[parentIdx].IMPORTANCE,
        value
      );
    } else {
      child[field] = value;
    }
    updated[parentIdx].CHILDREN[childIdx] = child;
    setFinancialCriteria(updated);
  };

  const handleParentChange = (parentIdx, field, value) => {
    const updated = deepClone(criteria);
    const parent = updated[parentIdx];
    if (field === "IMPORTANCE") {
      parent.IMPORTANCE = value;
      parent.CHILDREN = parent.CHILDREN.map((child) => ({
        ...child,
        IMPORTANCE: value,
        POINTS: getPointsFromMatrix(matrix, value, child.TYPE),
      }));
    } else {
      parent[field] = value;
    }
    updated[parentIdx] = parent;
    setCriteria(updated);
  };

  const handleChildChange = (parentIdx, childIdx, field, value) => {
    const updated = deepClone(criteria);
    const child = updated[parentIdx].CHILDREN[childIdx];
    if (field === "TYPE") {
      child.TYPE = value;
      child.POINTS = getPointsFromMatrix(
        matrix,
        updated[parentIdx].IMPORTANCE,
        value
      );
    } else {
      child[field] = value;
    }
    updated[parentIdx].CHILDREN[childIdx] = child;
    setCriteria(updated);
  };

  const handleMatrixChange = (idx, field, value) => {
    const updatedMatrix = deepClone(matrix);
    updatedMatrix[idx][field] = Number(value);

    const updatedCriteria = deepClone(criteria).map((parent) => ({
      ...parent,
      CHILDREN: (parent.CHILDREN || []).map((child) => ({
        ...child,
        POINTS: getPointsFromMatrix(
          updatedMatrix,
          parent.IMPORTANCE,
          child.TYPE
        ),
      })),
    }));

    setMatrix(updatedMatrix);
    setCriteria(updatedCriteria);
  };

  const handleSubmit = () => {
    setShowSaveModal(true);
  };
  const confirmSave = async () => {
    if (!saveName.trim()) {
      toast.error("Please enter a name before saving.");
      return;
    }

    setIsSubmitting(true);

    const payload = {
      data: [
        {
          id: recordId ?? "", // backend still expects this
          companyId: companyId ?? "",
          saveName: saveName.trim(),
          defaultCriteria: criteria,
          financialCriteria: financialCriteria,
          defaultMatrix: matrix,
        },
      ],
    };

    try {
      const saved = await saveCriteria(payload);

      // ✅ backend always inserts → capture new inserted_id
      const newId = saved?.inserted_id || saved?.id || recordId;
      if (newId) {
        setRecordId(newId);
      }

      const authoritative = saved?.defaultCriteria
        ? saved
        : {
            defaultCriteria: criteria,
            financialCriteria: financialCriteria,
            defaultMatrix: matrix,
            saveName: payload.data[0].saveName,
          };

      setCriteria(deepClone(authoritative.defaultCriteria));
      setSavedCriteria(deepClone(authoritative.defaultCriteria));

      setFinancialCriteria(deepClone(authoritative.financialCriteria));
      setSavedFinancialCriteria(deepClone(authoritative.financialCriteria));

      if (authoritative.defaultMatrix) {
        setMatrix(deepClone(authoritative.defaultMatrix));
      }

      setSaveName(authoritative.saveName || payload.data[0].saveName);

      setIsEditing(false);
      setShowSaveModal(false);
      toast.success(`Saved as "${payload.data[0].saveName}"`);
    } catch (err) {
      console.error("Error submitting:", err);
      toast.error("Failed to save data");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setCriteria(deepClone(savedCriteria));
    setFinancialCriteria(deepClone(savedFinancialCriteria));
    setMatrix(deepClone(defaultMatrix));
    setIsEditing(false);
  };

  // ----------------- Score Calculation -----------------
  const businessScore = getTotalScore(criteria);
  const financialScore = getTotalScore(FinancialCriteria);
  const combinedScore = {
    min: businessScore.minTotal + financialScore.minTotal,
    max: businessScore.maxTotal + financialScore.maxTotal,
  };
  const IndexToNormalize = {
    index: (100 / combinedScore.max).toFixed(2),
  };

  // ----------------- Render Helpers -----------------
  const renderParentRow = (row, idx) => (
    <tr style={{ backgroundColor: "#f8f9fa", fontWeight: "bold" }}>
      <td>{row.METRIC}</td>
      <td>
        {isEditing ? (
          <select
            className="form-control"
            value={row.IMPORTANCE}
            onChange={(e) =>
              handleParentChange(idx, "IMPORTANCE", e.target.value)
            }
          >
            <option>Low</option>
            <option>Medium</option>
            <option>Critical</option>
          </select>
        ) : (
          row.IMPORTANCE
        )}
      </td>
      <td colSpan="2" />
      <td>
        {isEditing ? (
          <>
            <input
              type="text"
              className="form-control mb-1"
              value={row.NOTES || ""}
              placeholder="Enter note"
              onChange={(e) => handleParentChange(idx, "NOTES", e.target.value)}
            />
            <input
              type="text"
              className="form-control"
              value={row.LINK || ""}
              placeholder="Enter link (optional)"
              onChange={(e) => handleParentChange(idx, "LINK", e.target.value)}
            />
          </>
        ) : row.LINK ? (
          <a href={row.LINK} target="_blank" rel="noopener noreferrer">
            {row.NOTES}
          </a>
        ) : (
          row.NOTES
        )}
      </td>
      <td colSpan="2" />
    </tr>
  );

  const renderChildRows = (row, parentIdx) => {
    const { min, max } = getMinMaxForParent(row.CHILDREN);
    return row.CHILDREN.map((child, cIdx) => {
      const isLast = cIdx === row.CHILDREN.length - 1;
      return (
        <tr key={cIdx}>
          <td style={{ paddingLeft: "40px" }}>{child.option}</td>
          <td>{row.IMPORTANCE}</td>
          <td>
            {isEditing ? (
              <select
                className="form-control"
                value={child.TYPE}
                onChange={(e) =>
                  handleChildChange(parentIdx, cIdx, "TYPE", e.target.value)
                }
              >
                <option>Positive</option>
                <option>Baseline</option>
                <option>Negative (low)</option>
                <option>Negative (high)</option>
              </select>
            ) : (
              child.TYPE
            )}
          </td>
          <td>{child.POINTS}</td>
          <td>
            {isEditing ? (
              <input
                className="form-control"
                value={child.NOTES}
                onChange={(e) =>
                  handleChildChange(parentIdx, cIdx, "NOTES", e.target.value)
                }
              />
            ) : (
              child.NOTES
            )}
          </td>
          <td>{isLast ? min : ""}</td>
          <td>{isLast ? max : ""}</td>
        </tr>
      );
    });
  };
  const renderParentRowFinancial = (row, idx) => (
    <tr key={idx} style={{ backgroundColor: "#f9f9f9" }}>
      <td>{row.METRIC}</td>
      <td>
        {isEditing ? (
          <select
            className="form-control"
            value={row.IMPORTANCE}
            onChange={(e) =>
              handleParentChangeFinancial(idx, "IMPORTANCE", e.target.value)
            }
          >
            <option>High</option>
            <option>Medium</option>
            <option>Low</option>
          </select>
        ) : (
          row.IMPORTANCE
        )}
      </td>
      <td colSpan="2"></td>
      <td>
        {isEditing ? (
          <>
            <input
              type="text"
              className="form-control mb-1"
              value={row.NOTES || ""}
              placeholder="Enter note"
              onChange={(e) =>
                handleParentChangeFinancial(idx, "NOTES", e.target.value)
              }
            />
            <input
              type="text"
              className="form-control"
              value={row.LINK || ""}
              placeholder="Enter link (optional)"
              onChange={(e) =>
                handleParentChangeFinancial(idx, "LINK", e.target.value)
              }
            />
          </>
        ) : row.LINK ? (
          <a href={row.LINK} target="_blank" rel="noopener noreferrer">
            {row.NOTES}
          </a>
        ) : (
          row.NOTES
        )}
      </td>
      <td colSpan="2" />
    </tr>
  );

  const renderChildRowsFinancial = (row, parentIdx) => {
    const { min, max } = getMinMaxForParent(row.CHILDREN);
    return row.CHILDREN.map((child, cIdx) => {
      const isLast = cIdx === row.CHILDREN.length - 1;
      return (
        <tr key={cIdx}>
          <td style={{ paddingLeft: "40px" }}>{child.option}</td>
          <td>{row.IMPORTANCE}</td>
          <td>
            {isEditing ? (
              <select
                className="form-control"
                value={child.TYPE}
                onChange={(e) =>
                  handleChildChangeFinancial(
                    parentIdx,
                    cIdx,
                    "TYPE",
                    e.target.value
                  )
                }
              >
                <option>Positive</option>
                <option>Baseline</option>
                <option>Negative (low)</option>
                <option>Negative (high)</option>
              </select>
            ) : (
              child.TYPE
            )}
          </td>
          <td>{child.POINTS}</td>
          <td>
            {isEditing ? (
              <input
                className="form-control"
                value={child.NOTES}
                onChange={(e) =>
                  handleChildChangeFinancial(
                    parentIdx,
                    cIdx,
                    "NOTES",
                    e.target.value
                  )
                }
              />
            ) : (
              child.NOTES
            )}
          </td>
          <td>{isLast ? min : ""}</td>
          <td>{isLast ? max : ""}</td>
        </tr>
      );
    });
  };

  // ----------------- JSX -----------------
  return (
    <div className="app-container">
      <Toaster />
      <div className="container-fluid">
        <main className="content">
          <div className="row">
            <Navbar />
            <div className="col-md-10 mt-4">
              <NavLink to={`/Dashboard`} className="btn btn-dark ms-2">
                Back to Company Log
              </NavLink>
              <NavLink
                to={`/details/${id}`}
                className="btn btn-outline-secondary ms-2"
              >
                Company Information
              </NavLink>
              <NavLink
                to={`/DocumentCheckList/${id}`}
                className="btn btn-outline-secondary ms-2"
              >
                Document Checklist
              </NavLink>
              <NavLink
                to={`/Searches/${id}`}
                className="btn btn-outline-secondary ms-2"
              >
                Searches
              </NavLink>
              <NavLink
                to={`/FinancialAnalysis/${id}`}
                className="btn btn-outline-secondary ms-2"
              >
                Financial Analysis
              </NavLink>
              <NavLink
                to={`/CompanyScore/${id}`}
                className="btn btn-outline-secondary ms-2"
              >
                Company Score
              </NavLink>
              <NavLink
                to={`/ScoringCriteria/${id}`}
                className="btn btn-dark ms-2"
              >
                Scoring Criteria
              </NavLink>

              <h3 className="mt-3">Scoring Criteria</h3>

              <div className="d-flex justify-content-end mb-3">
                {isEditing ? (
                  <>
                    <button
                      className="btn btn-success me-2"
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Saving..." : "Save"}
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={handleCancel}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    className="btn btn-primary"
                    onClick={() => setIsEditing(true)}
                  >
                    Edit
                  </button>
                )}
              </div>

              {/* Render Tables */}
              {/* Business Score Table */}
              <h5 className="fw-bold">Business Score</h5>
              <table className="table table-bordered">
                <thead>
                  <tr
                    className="text-white"
                    style={{ backgroundColor: "#173660" }}
                  >
                    <th>Criteria</th>
                    <th>Importance</th>
                    <th>Type</th>
                    <th>New Point</th>
                    <th>Notes & Definitions</th>
                    <th>Min</th>
                    <th>Max</th>
                  </tr>
                </thead>
                <tbody>
                  {criteria.map((row, idx) => (
                    <React.Fragment key={idx}>
                      {renderParentRow(row, idx)}
                      {renderChildRows(row, idx)}
                    </React.Fragment>
                  ))}
                </tbody>
                <tfoot>
                  <tr
                    className="fw-bold"
                    style={{ backgroundColor: "lightsalmon" }}
                  >
                    <td colSpan="5">Total Score</td>
                    <td>{businessScore.minTotal}</td>
                    <td>{businessScore.maxTotal}</td>
                  </tr>
                </tfoot>
              </table>

              {/* Financial Score Table */}
              <h5 className="fw-bold">Financial Score</h5>
              <table className="table table-bordered">
                <thead>
                  <tr
                    className="text-white"
                    style={{ backgroundColor: "#173660" }}
                  >
                    <th>Criteria</th>
                    <th>Importance</th>
                    <th>Type</th>
                    <th>New Point</th>
                    <th>Notes & Definitions</th>
                    <th>Min</th>
                    <th>Max</th>
                  </tr>
                </thead>
                <tbody>
                  {financialCriteria.map((row, idx) => (
                    <React.Fragment key={idx}>
                      {renderParentRowFinancial(row, idx)}
                      {renderChildRowsFinancial(row, idx)}
                    </React.Fragment>
                  ))}
                </tbody>
                <tfoot>
                  <tr
                    style={{
                      backgroundColor: "lightsalmon",
                      fontWeight: "bold",
                    }}
                  >
                    <td colSpan="5">Total Score</td>
                    <td>{financialScore.minTotal}</td>
                    <td>{financialScore.maxTotal}</td>
                  </tr>
                  <tr
                    style={{
                      backgroundColor: "lightskyblue",
                      fontWeight: "bold",
                    }}
                  >
                    <td colSpan="5">Combined Company Score</td>
                    <td>{combinedScore.min}</td>
                    <td>{combinedScore.max}</td>
                  </tr>
                  <tr
                    style={{ backgroundColor: "lightblue", fontWeight: "bold" }}
                  >
                    <td colSpan="5">Index to Normalize</td>
                    <td>{IndexToNormalize.index}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
              {/* other table */}
              <table className="table table-bordered">
                <thead>
                  <tr style={{ backgroundColor: "#173660", color: "#fff" }}>
                    <th>Range</th>
                    <th>Implications</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Between 70 and 100 points</td>
                    <td>
                      Lowest risk category and probability of default. Clients
                      suitable for loan financing.
                    </td>
                  </tr>
                  <tr>
                    <td>Between 40 and 69 points</td>
                    <td>
                      Average risk category and probability of default. Clients
                      suitable for factoring transactions and loan financing
                      with conservative security and monitoring provisions.
                    </td>
                  </tr>
                  <tr>
                    <td>Between 0 and 39 points</td>
                    <td>
                      High-risk category and probability of default. Clients
                      could be suitable for factoring transactions and loan
                      financing with very conservative security and monitoring
                      provisions.{" "}
                    </td>
                  </tr>
                  <tr>
                    <td>Less than 0 points</td>
                    <td>
                      Highest risk category and probability of default. Clients
                      should not be financed unless with explicit exception
                      granted by management.
                    </td>
                  </tr>
                  <tr>
                    <td></td>
                  </tr>
                  <tr>
                    <td style={{ textAlign: "center" }}>0</td>
                    <td
                      style={{
                        textAlign: "center",
                        backgroundColor: "red",
                        color: "#fff",
                      }}
                    >
                      D
                    </td>
                  </tr>
                  <tr>
                    <td style={{ textAlign: "center" }}>39</td>
                    <td
                      style={{
                        textAlign: "center",
                        backgroundColor: "yellow",
                        color: "black",
                      }}
                    >
                      C
                    </td>
                  </tr>
                  <tr>
                    <td style={{ textAlign: "center" }}>69</td>
                    <td
                      style={{
                        textAlign: "center",
                        backgroundColor: "lightgreen",
                        color: "black",
                      }}
                    >
                      B
                    </td>
                  </tr>
                  <tr>
                    <td style={{ textAlign: "center" }}>100</td>
                    <td
                      style={{
                        textAlign: "center",
                        backgroundColor: "lightgreen",
                        color: "black",
                      }}
                    >
                      A
                    </td>
                  </tr>
                </tbody>
              </table>

              <table className="table table-bordered">
                <thead>
                  <tr style={{ backgroundColor: "#173660", color: "#fff" }}>
                    <th colSpan="6" style={{ backgroundColor: "lightskyblue" }}>
                      Debtor's Rating
                    </th>
                  </tr>
                  <tr>
                    <td>Company Type</td>
                    <td></td>
                    <td>Indicators</td>
                    <td
                      style={{
                        backgroundColor: "lightgreen",
                        textAlign: "center",
                      }}
                    >
                      A
                    </td>
                    <td
                      style={{
                        backgroundColor: "lightgreen",
                        textAlign: "center",
                      }}
                    >
                      B
                    </td>
                    <td
                      style={{
                        backgroundColor: "lightyellow",
                        textAlign: "center",
                      }}
                    >
                      C
                    </td>
                    <td
                      style={{
                        backgroundColor: "lightcoral",
                        textAlign: "center",
                      }}
                    >
                      D
                    </td>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Public / Private</td>
                    <td style={{ textAlign: "right" }}>100%</td>
                    <td
                      style={{
                        backgroundColor: "lightpink",
                        textAlign: "center",
                      }}
                    >
                      D&B point system
                    </td>
                    <td
                      style={{
                        backgroundColor: "lightpink",
                        textAlign: "center",
                      }}
                    >
                      5
                    </td>
                    <td
                      style={{
                        backgroundColor: "lightpink",
                        textAlign: "center",
                      }}
                    >
                      10
                    </td>
                    <td
                      style={{
                        backgroundColor: "lightpink",
                        textAlign: "center",
                      }}
                    >
                      15
                    </td>
                    <td
                      style={{
                        backgroundColor: "lightpink",
                        textAlign: "center",
                      }}
                    >
                      20
                    </td>
                  </tr>
                  <tr>
                    <td>Public / Private</td>
                    <td style={{ textAlign: "right" }}>65%</td>
                    <td
                      style={{
                        backgroundColor: "lightpink",
                        textAlign: "center",
                      }}
                    >
                      D&B risk level
                    </td>
                    <td
                      style={{
                        backgroundColor: "lightpink",
                        textAlign: "center",
                      }}
                    >
                      1
                    </td>
                    <td
                      style={{
                        backgroundColor: "lightpink",
                        textAlign: "center",
                      }}
                    >
                      2
                    </td>
                    <td
                      style={{
                        backgroundColor: "lightpink",
                        textAlign: "center",
                      }}
                    >
                      3
                    </td>
                    <td
                      style={{
                        backgroundColor: "lightpink",
                        textAlign: "center",
                      }}
                    >
                      4
                    </td>
                  </tr>
                  <tr>
                    <td>Public / Private</td>
                    <td style={{ textAlign: "right" }}>25%</td>
                    <td
                      style={{
                        backgroundColor: "lightpink",
                        textAlign: "center",
                      }}
                    >
                      D&B Paydex
                    </td>
                    <td
                      style={{
                        backgroundColor: "lightpink",
                        textAlign: "center",
                      }}
                    >
                      73-100
                    </td>
                    <td
                      style={{
                        backgroundColor: "lightpink",
                        textAlign: "center",
                      }}
                    >
                      63-72
                    </td>
                    <td
                      style={{
                        backgroundColor: "lightpink",
                        textAlign: "center",
                      }}
                    >
                      56-62
                    </td>
                    <td
                      style={{
                        backgroundColor: "lightpink",
                        textAlign: "center",
                      }}
                    >
                      0-55
                    </td>
                  </tr>
                  <tr>
                    <td>Public / Private</td>
                    <td style={{ textAlign: "right" }}>15%</td>
                    <td
                      style={{
                        backgroundColor: "lightpink",
                        textAlign: "center",
                      }}
                    >
                      D&B financial strength
                    </td>
                    <td
                      style={{
                        backgroundColor: "lightpink",
                        textAlign: "center",
                      }}
                    >
                      4A, 5A
                    </td>
                    <td
                      style={{
                        backgroundColor: "lightpink",
                        textAlign: "center",
                      }}
                    >
                      3A
                    </td>
                    <td
                      style={{
                        backgroundColor: "lightpink",
                        textAlign: "center",
                      }}
                    >
                      2A
                    </td>
                    <td
                      style={{
                        backgroundColor: "lightpink",
                        textAlign: "center",
                      }}
                    >
                      {" "}
                      1A & below
                    </td>
                  </tr>
                  <tr>
                    <td>Public</td>
                    <td></td>
                    <td style={{ textAlign: "center" }}>Top 3 RA</td>
                    <td style={{ textAlign: "center" }}>&gt;= A-</td>
                    <td style={{ textAlign: "center" }}>&gt;= BBB-</td>
                    <td style={{ textAlign: "center" }}>&gt; C</td>
                    <td style={{ textAlign: "center" }}> D</td>
                  </tr>
                  <tr>
                    <td>Public (if no RA)</td>
                    <td></td>
                    <td style={{ textAlign: "center" }}>D&B weighted rating</td>
                    <td style={{ textAlign: "center" }}>5 to 9</td>
                    <td style={{ textAlign: "center" }}>10 to 14</td>
                    <td style={{ textAlign: "center" }}>15 to 19</td>
                    <td style={{ textAlign: "center" }}> 20</td>
                  </tr>
                  <tr>
                    <td>Private</td>
                    <td></td>
                    <td style={{ textAlign: "center" }}>Coface rating</td>
                    <td style={{ textAlign: "center" }}>7 to 10</td>
                    <td style={{ textAlign: "center" }}>6</td>
                    <td style={{ textAlign: "center" }}>4, 5</td>
                    <td style={{ textAlign: "center" }}> 1 to 3</td>
                  </tr>
                  <tr>
                    <td></td>
                    <td></td>
                    <td style={{ textAlign: "center" }}>
                      Riverchain's Client Card (when financials are available)
                    </td>
                    <td style={{ textAlign: "center" }}>
                      Between 70 and 100 points
                    </td>
                    <td style={{ textAlign: "center" }}>
                      Between 40 and 69 points
                    </td>
                    <td style={{ textAlign: "center" }}>
                      "Between 0 and 39 points / No financials, no Rating
                      Agency, no D&B, Coface"
                    </td>
                    <td style={{ textAlign: "center" }}> Less than 0 points</td>
                  </tr>
                </tbody>
              </table>

              {/* Matrix Table */}
              <h5 className="fw-bold">Scoring Matrix</h5>
              <table className="table table-bordered">
                <thead>
                  <tr
                    className="text-white"
                    style={{ backgroundColor: "#173660" }}
                  >
                    <th>Factor Importance</th>
                    <th>Positive</th>
                    <th>Baseline</th>
                    <th>Negative (low)</th>
                    <th>Negative (high)</th>
                  </tr>
                </thead>
                <tbody>
                  {matrix.map((row, idx) => (
                    <tr key={idx}>
                      <td>{row.importance}</td>
                      {[
                        "positive",
                        "baseline",
                        "negativeLow",
                        "negativeHigh",
                      ].map((field) => (
                        <td key={field}>
                          {isEditing ? (
                            <input
                              type="number"
                              className="form-control"
                              value={row[field]}
                              onChange={(e) =>
                                handleMatrixChange(idx, field, e.target.value)
                              }
                            />
                          ) : (
                            row[field]
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>
      {showSaveModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1050,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 8,
              padding: 20,
              minWidth: 320,
              maxWidth: "90%",
              boxShadow: "0 6px 24px rgba(0,0,0,0.3)",
            }}
          >
            <h5>Save Changes</h5>
            <p>Enter a name for this save:</p>

            <input
              type="text"
              className="form-control"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              placeholder="e.g. Draft - Sept 2025"
            />

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                marginTop: 12,
              }}
            >
              <button
                className="btn btn-secondary"
                onClick={() => setShowSaveModal(false)}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={confirmSave}
                disabled={isSubmitting || !saveName.trim()}
              >
                {isSubmitting ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
